import { Link, useBlocker, useNavigate } from "@tanstack/react-router";
import type { SequenceTriggerType, SequenceType } from "@unbox/api";
import { Announcement03, ArrowLeft, Play, Save01 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { APIError } from "@/api/error";
import {
  useCreateSequence,
  useDeleteSequenceDefault,
  useSequence,
  useSequenceDefaults,
  useSetSequenceDefault,
  useUpdateSequence,
} from "@/api/sequences";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Toggle } from "@/components/base/toggle/toggle";
import { Button as ShadButton } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMerchant } from "@/providers/MerchantProvider";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { ApplyToAllDialog } from "./apply-to-all-dialog";
import { TRIGGER_EVENT_LABELS, TRIGGER_LABELS, TRIGGER_SCENARIOS } from "./sequence-steps-preview";
import { collapseForPersist, expandPersistedSteps, SequenceCanvas, type Step } from "./step-builder";

function validateSteps(steps: Step[]): string | null {
  if (steps.length === 0) return "Add at least one step before saving.";
  let actionIndex = 0;
  for (const s of steps) {
    if (s.type === "wait") continue;
    actionIndex += 1;
    if (s.type === "send_message" && !s.messageTemplateId && !s.customMessage) {
      return `Step ${actionIndex} (Send Message) needs a message. Pick a template or write a custom one.`;
    }
  }
  return null;
}

const TRIGGER_SUPPORTING: Record<string, string> = {
  safety_action: "Triggered when a reviewer creates a safety action",
  collaboration_invite: "Invitation message defined in collaboration settings",
  sample_delivered: "",
  creator_dormant: "",
};

function sequenceTypeFromTrigger(triggerType?: string): SequenceType | null {
  if (triggerType === "safety_action") return "safety";
  if (triggerType) return "creator";
  return null;
}

export function SequenceDetail({ sequenceId, setDefaultFor }: { sequenceId: string; setDefaultFor?: string }) {
  const isNew = sequenceId === "new";
  const navigate = useNavigate();
  const merchantId = useMerchant();
  const { data: sequence, isLoading } = useSequence(sequenceId, { enabled: !isNew });
  const updateSequence = useUpdateSequence(sequenceId);
  const createSequence = useCreateSequence();
  const setDefault = useSetSequenceDefault();
  const deleteDefault = useDeleteSequenceDefault();
  const { data: defaults = [] } = useSequenceDefaults();

  const [applyToAllOpen, setApplyToAllOpen] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [savedSteps, setSavedSteps] = useState<Step[]>([]);
  const [sequenceType, setSequenceType] = useState<SequenceType | null>(sequenceTypeFromTrigger(setDefaultFor));

  const savedStepsRef = useRef(savedSteps);
  savedStepsRef.current = savedSteps;

  useEffect(() => {
    if (sequence?.steps) {
      const expanded = expandPersistedSteps(sequence.steps);
      if (JSON.stringify(expanded) !== JSON.stringify(savedStepsRef.current)) {
        setSteps(expanded);
        setSavedSteps(expanded);
      }
    }
    if (sequence) {
      setSequenceType(sequence.type as SequenceType);
    }
  }, [sequence]);

  const isDirty = JSON.stringify(steps) !== JSON.stringify(savedSteps);

  const blocker = useBlocker({
    shouldBlockFn: () => !isNew && isDirty,
    enableBeforeUnload: () => !isNew && isDirty,
    withResolver: true,
  });

  const linkedDefault = !isNew ? defaults.find((d) => d.sequenceId === sequenceId) : undefined;

  const activeTriggerType =
    (setDefaultFor as SequenceTriggerType | undefined) ??
    (linkedDefault?.triggerType as SequenceTriggerType | undefined);
  const isActive = !!linkedDefault?.enabled;
  const isCreatorTrigger = !!activeTriggerType && activeTriggerType !== "safety_action";

  const triggerEventLabel = activeTriggerType
    ? (TRIGGER_EVENT_LABELS[activeTriggerType] ?? activeTriggerType)
    : "Trigger";
  const triggerSupporting = activeTriggerType ? TRIGGER_SUPPORTING[activeTriggerType] : undefined;
  const triggerLabel = activeTriggerType ? TRIGGER_LABELS[activeTriggerType] : "";

  const deriveName = () => triggerLabel || (sequenceType ? `${sequenceType} sequence` : "Sequence");

  const createWithName = async (name: string, applyToExisting: boolean) => {
    if (!sequenceType) return null;
    const created = await createSequence.mutateAsync({
      name,
      type: sequenceType,
      triggerType: setDefaultFor as
        | "safety_action"
        | "collaboration_invite"
        | "sample_delivered"
        | "creator_dormant"
        | undefined,
      steps: collapseForPersist(steps),
    });
    if (setDefaultFor) {
      await setDefault.mutateAsync({
        triggerType: setDefaultFor,
        sequenceId: created.id,
        applyToExisting,
      });
    }
    return created;
  };

  const persistSteps = async () => {
    const stepError = validateSteps(steps);
    if (stepError) {
      toastError(stepError);
      return;
    }
    if (isNew) {
      if (!sequenceType) {
        toastError("Pick a sequence type before saving.");
        return;
      }
      if (isCreatorTrigger) {
        setApplyToAllOpen(true);
        return;
      }
      try {
        const created = await createWithName(deriveName(), false);
        if (created) {
          navigate({
            to: "/merchant/$merchantId/automate-outreach/$sequenceId",
            params: { merchantId, sequenceId: created.id },
            replace: true,
          });
        }
      } catch (err) {
        if (!(err instanceof APIError)) toastError("Failed to create sequence");
      }
      return;
    }
    try {
      await updateSequence.mutateAsync({ steps: collapseForPersist(steps) });
      setSavedSteps(steps);
      toastSuccess(
        "Sequence saved. Changes apply to new runs only. Runs already in progress keep their current steps.",
      );
    } catch (err) {
      // APIErrors are toasted by the global mutationCache handler
      if (!(err instanceof APIError)) toastError("Failed to save sequence");
    }
  };

  const handleApplyToAllChoose = async (applyToExisting: boolean) => {
    try {
      const created = await createWithName(deriveName(), applyToExisting);
      setApplyToAllOpen(false);
      if (created) {
        navigate({
          to: "/merchant/$merchantId/automate-outreach/$sequenceId",
          params: { merchantId, sequenceId: created.id },
          replace: true,
        });
      }
    } catch (err) {
      if (!(err instanceof APIError)) toastError("Failed to create sequence");
    }
  };

  const handleActivateToggle = async (next: boolean) => {
    if (!activeTriggerType) return;
    if (next) {
      if (isCreatorTrigger) {
        setApplyToAllOpen(true);
        return;
      }
      try {
        await setDefault.mutateAsync({ triggerType: activeTriggerType, sequenceId, applyToExisting: false });
        toastSuccess("Automation activated");
      } catch (err) {
        if (!(err instanceof APIError)) toastError("Failed to activate automation");
      }
    } else {
      try {
        await deleteDefault.mutateAsync(activeTriggerType);
        toastSuccess("Automation deactivated");
      } catch (err) {
        if (!(err instanceof APIError)) toastError("Failed to deactivate automation");
      }
    }
  };

  const handleActivateForExisting = async (applyToExisting: boolean) => {
    if (!activeTriggerType) return;
    try {
      await setDefault.mutateAsync({
        triggerType: activeTriggerType,
        sequenceId,
        applyToExisting,
      });
      setApplyToAllOpen(false);
      toastSuccess("Automation activated");
    } catch (err) {
      if (!(err instanceof APIError)) toastError("Failed to activate automation");
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingIndicator />
      </div>
    );
  }

  if (!isNew && !sequence) {
    return <div className="flex items-center justify-center min-h-96 text-gray-500">Sequence not found</div>;
  }

  const title = isNew ? triggerLabel || "New Sequence" : (sequence?.name ?? "");
  const canSave = steps.length > 0 && (isNew ? !!sequenceType : isDirty);

  return (
    <div className="flex flex-col w-full h-screen items-center bg-primary">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col h-full px-16 pt-16 pb-8 gap-8`}>
        {/* Header */}
        <div className="flex flex-row items-center justify-between flex-shrink-0 gap-8">
          <div className="flex flex-col gap-1 min-w-[320px] flex-1">
            <h1 className="text-lg font-medium text-primary flex items-center gap-1.5">
              <Announcement03 className="w-5 h-5" />
              {title}
            </h1>
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-semibold text-tertiary hover:text-secondary cursor-pointer"
              onClick={() => navigate({ to: "/merchant/$merchantId/automate-outreach", params: { merchantId } })}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all Outreach automations
            </button>
          </div>

          <div className="flex items-center gap-4">
            {!isNew && isDirty && (
              <Badge size="sm" color="warning">
                Unsaved Changes
              </Badge>
            )}

            {!isNew && activeTriggerType && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-secondary">Activate automation</span>
                <Toggle
                  size="md"
                  isSelected={isActive}
                  onChange={handleActivateToggle}
                  aria-label="Activate automation"
                />
              </div>
            )}

            {!isNew && (
              <ShadButton asChild variant="outline" size="sm" className="text-primary">
                <Link
                  to="/merchant/$merchantId/automate-outreach/runs"
                  params={{ merchantId }}
                  search={{ sequenceRunSequence: sequenceId }}
                >
                  View Runs
                </Link>
              </ShadButton>
            )}

            <Button
              size="sm"
              iconLeading={<Save01 className="w-4 h-4" />}
              onClick={persistSteps}
              isDisabled={!canSave}
              isLoading={isNew ? createSequence.isPending : updateSequence.isPending}
            >
              Save
            </Button>
          </div>
        </div>

        {/* Sequence type picker (new only) */}
        {isNew && !setDefaultFor && (
          <div className="rounded-md border border-secondary bg-tertiary px-4 py-3 flex items-center gap-3">
            <span className="text-sm font-medium text-secondary">Sequence type</span>
            <Select value={sequenceType ?? ""} onValueChange={(v) => setSequenceType(v as SequenceType)}>
              <SelectTrigger className="w-60 h-9 text-sm">
                <SelectValue placeholder="Select a type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="creator">Creator Sequence</SelectItem>
              </SelectContent>
            </Select>
            {sequenceType && TRIGGER_SCENARIOS[sequenceType] && (
              <span className="text-xs text-tertiary">{TRIGGER_SCENARIOS[sequenceType].join(", ")}</span>
            )}
          </div>
        )}

        {/* Canvas */}
        <div className="flex-1 rounded-2xl bg-secondary_alt overflow-auto">
          <div className="flex justify-center py-10 px-10">
            <SequenceCanvas
              steps={steps}
              onStepsChange={setSteps}
              triggerEventLabel={triggerEventLabel}
              triggerSupporting={triggerSupporting}
              triggerIcon={<Play className="w-4 h-4" />}
            />
          </div>
        </div>
      </div>

      <ApplyToAllDialog
        open={applyToAllOpen}
        onOpenChange={setApplyToAllOpen}
        onChoose={isNew ? handleApplyToAllChoose : handleActivateForExisting}
        isPending={createSequence.isPending || setDefault.isPending}
      />

      <Dialog open={blocker.status === "blocked"} onOpenChange={() => blocker.status === "blocked" && blocker.reset()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-secondary">You have unsaved changes. Are you sure you want to leave this page?</p>
          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => blocker.status === "blocked" && blocker.reset()}>
              Stay
            </Button>
            <Button size="sm" onClick={() => blocker.status === "blocked" && blocker.proceed()}>
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
